/**
 * `WorkerStepOne` — ADR-0212 Rule 2. Inside the WordPress / WooCommerce
 * tab, when the site is served THROUGH Cloudflare (orange cloud), Step 1
 * is the Worker, not the plugin: Cloudflare serves cached pages before
 * they reach PHP, so the plugin misses exactly the hits the cache
 * serves. The store step (Woo) below is untouched. One escape hatch
 * back to the plugin; hidden once the Worker is deployed.
 */

import { ArrowRight } from 'lucide-react';
import type { ReactNode } from 'react';

import { useC } from '../../lib/theme.js';
import { StepHeading } from '../../components/atoms/index.js';
import { WORKER_INSTEAD_SENTENCE } from './install-copy.js';

const sansFont = "'Geist', sans-serif";

export function WorkerStepOne({
  workerPanel,
  deployed,
  onPreferPlugin,
  testidPrefix,
  showStepBadge = true,
}: {
  readonly workerPanel: ReactNode;
  readonly deployed: boolean;
  readonly onPreferPlugin: () => void;
  readonly testidPrefix: 'wp' | 'woo';
  /** When `false`, the Worker step heading renders without a numbered
   *  badge — the WordPress + Cloudflare case where the Worker is the
   *  one visible step (founder 2026-10-21). WooCommerce keeps the
   *  badge because Step 2 (Connect the store) still follows below. */
  readonly showStepBadge?: boolean;
}): JSX.Element {
  const C = useC();
  return (
    <div className="mb-4" data-testid={`${testidPrefix}-worker-step-one`}>
      {showStepBadge ? (
        <StepHeading n={1}>Deploy the Cloudflare Worker</StepHeading>
      ) : (
        <StepHeading>Deploy the Cloudflare Worker</StepHeading>
      )}
      <p style={{ fontFamily: sansFont, fontSize: 14, color: C.text, lineHeight: 1.55, margin: '0 0 12px', maxWidth: '60ch' }}>
        {WORKER_INSTEAD_SENTENCE}
      </p>
      {workerPanel}
      {deployed ? null : (
        <button
          type="button"
          onClick={onPreferPlugin}
          className="inline-flex items-center gap-1 mt-3"
          style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontFamily: sansFont, fontSize: 13, color: C.muted }}
          data-testid={`${testidPrefix}-prefer-plugin`}
        >
          Prefer the plugin?{' '}
          <span style={{ color: C.text, fontWeight: 500 }}>Install it instead</span>
          <ArrowRight size={12} />
        </button>
      )}
    </div>
  );
}
